/**
 * Mucka's M, in whatever colour the row around it is.
 *
 * ## Why a masked PNG and not a vector
 *
 * Two vector routes were tried and measured against the artwork before this
 * one, and both are recorded here so nobody spends the afternoon again:
 *
 *  1. **Drawing it on lucide's 24×24 grid.** My own stroked M. It looked like an
 *     M and it was not Mucka's — the mark is a solid monogram in a box, wider
 *     than tall (130×90), and re-drawing a customer-facing letterform by eye is
 *     how a brand mark quietly becomes a near-miss of itself.
 *  2. **`public/brand/mucka-icon-m.svg`.** A potrace of the same artwork that is
 *     POLARITY-INVERTED: it fills the background and knocks the M out.
 *     Rendered and scored against `mucka-icon-white.png`, it overlaps the real
 *     mark by **IoU 0.28**, and neither fill rule changes that. Flipping it with
 *     an enclosing rectangle gets the strokes back but loses the frame —
 *     **IoU 0.22**, worse. It is a single contour of the negative space and
 *     there is no correct fill rule for it.
 *
 * So the artwork itself is the source: `mucka-icon-white.png` is white on
 * transparent, which makes its ALPHA CHANNEL exactly the letter. Used as a
 * mask over `currentColor`, it is pixel-faithful to the brand and takes the
 * colour of the row it sits in — including hover, active and dark mode, without
 * a second asset for any of them. That is the whole reason to mask rather than
 * to `<img>` a white PNG, which would be white on a white settings page.
 *
 * ## 🚨 It still has to BE a `LucideIcon`
 *
 * `SettingsSection.icon` declares that type and `<Icon>` renders it. So the
 * props are matched by hand — `size` becomes the box, `color` overrides the
 * inherited colour, and `strokeWidth` is accepted and thrown away, because a
 * masked shape has no stroke and every caller passes one. The export is cast
 * because the element is a `<span>`, not an `<svg>`; nothing passes a ref to
 * these icons, and forwarding one to the wrong element type would be a lie the
 * types could not catch.
 *
 * 🚨 No `"use client"`, ever. `registry.ts` imports this and is read on both
 * sides of the server boundary — a directive here makes every export a throwing
 * proxy on the server and 500s the settings routes, with nothing in the types
 * to say why (CLAUDE.md anti-pattern #18).
 */
import type { CSSProperties } from 'react';
import type { LucideIcon, LucideProps } from 'lucide-react';

/** The brand mark. White on transparent, so its alpha IS the letter. */
const MARK = '/mucka-icon-white.png';

function MuckaMIcon({
  // lucide's own props, none of which are DOM attributes.
  size = 24,
  color,
  absoluteStrokeWidth: _absoluteStrokeWidth,
  strokeWidth: _strokeWidth,
  className,
  style,
}: LucideProps) {
  const mask: CSSProperties = {
    display: 'inline-block',
    flex: 'none',
    width: size,
    height: size,
    // The colour comes from here, and this is the point of the whole file.
    backgroundColor: color ?? 'currentColor',
    WebkitMaskImage: `url(${MARK})`,
    maskImage: `url(${MARK})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    // `contain`, so the 130×90 mark letterboxes inside a square box rather than
    // being stretched. The box stays square at whatever `size` the caller asks
    // for because the rail lays its rows out on a fixed icon box — a glyph that
    // reported its own 1.44:1 would shunt one label out of line with the rest.
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    ...style,
  };

  // Decorative: every place this renders has a text label beside it.
  return <span aria-hidden="true" className={className} style={mask} />;
}

export const MuckaM = MuckaMIcon as unknown as LucideIcon;
export default MuckaM;
